import _ from 'lodash'
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from 'react'
import { View, ScrollView } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import entityActions, { getFilterString } from '../../store/entity/actions'
import { shouldRefetch } from './RadioList'
import { sanitiseFilterString } from '../../libraries/sanitiseFilterString'
import { DataTable } from 'react-native-paper'
import { MSFESCheckbox } from '../MSFESCheckbox'
import PublicFormContext from '../../contexts/PublicFormContext'
import { useServerFilter } from '../../hooks/useServerFilter'

const useFetchEntitiesOfType = (entityType, args = {}) => {
  const { filterRule, needsFieldsToSort, serverFilter } = args
  const filter = filterRule || needsFieldsToSort ? { detailed: true } : null

  const filterString = sanitiseFilterString(
    getFilterString(serverFilter, filter)
  )

  const publicFormContext = useContext(PublicFormContext)

  const dispatch = useDispatch()
  const sourceListRoot = useSelector((state) => {
    return entityType?.name
      ? _.get(state.entities, `[${entityType?.name + filterString}]`, {
          data: []
        })
      : { data: [] }
  })
  const entities = sourceListRoot?.data ?? []

  const [nextPage, setNextPage] = useState(1)
  const maxPages = 5
  const fetchingRef = useRef(false)

  const fetchPage = useCallback(
    (page) => {
      if (!entityType?.name || fetchingRef.current) return
      if (page > maxPages) {
        setNextPage(-1)
        return
      }

      fetchingRef.current = true
      dispatch(
        entityActions.listEntity(
          { name: entityType.name },
          {
            filter: {
              ...filter,
              ...serverFilter
            },
            page: page,
            detailed: filter?.detailed,
            isFullRefresh: page === 1
          },
          undefined,
          publicFormContext.entityType,
          {
            tenantId: publicFormContext.tenantId,
            tenantHmac: publicFormContext.tenantHmac
          }
        )
      )
        .then((response) => {
          fetchingRef.current = false
          if (
            response.meta.current_page < response.meta.last_page &&
            response.meta.current_page < maxPages
          ) {
            // Automatically fetch next page
            setNextPage(response.meta.current_page + 1)
          } else {
            setNextPage(-1)
          }
        })
        .catch(() => {
          fetchingRef.current = false
          setNextPage(-1)
        })
    },
    [
      entityType?.name,
      filter,
      serverFilter,
      publicFormContext?.entityType,
      publicFormContext?.tenantId,
      publicFormContext?.tenantHmac,
      dispatch
    ]
  )

  useEffect(() => {
    if (
      entityType?.name &&
      shouldRefetch(sourceListRoot) &&
      !fetchingRef.current
    ) {
      setNextPage(1)
      fetchPage(1)
    }
  }, [serverFilter, entityType?.name, fetchPage])

  useEffect(() => {
    if (nextPage > 1 && nextPage <= maxPages && !fetchingRef.current) {
      fetchPage(nextPage)
    }
  }, [nextPage, fetchPage, maxPages])

  return { data: entities }
}
const _RadioTable = (props, ref) => {
  const { editable = true } = props
  const { columnHeaders, rowHeaders } =
    props.data?.field?.field_data?.params ?? {}

  const initialServerFilter =
    props.data?.field?.field_data?.params?.serverFilter

  const { serverFilter: columnServerFilter } = useServerFilter(
    columnHeaders,
    initialServerFilter
  )
  const { serverFilter: rowServerFilter } = useServerFilter(
    rowHeaders,
    initialServerFilter
  )

  const columnEntityType = useMemo(() => ({ name: columnHeaders }), [
    columnHeaders
  ])
  const rowEntityType = useMemo(() => ({ name: rowHeaders }), [rowHeaders])

  const columnArgs = useMemo(() => ({ serverFilter: columnServerFilter }), [
    columnServerFilter
  ])
  const rowArgs = useMemo(() => ({ serverFilter: rowServerFilter }), [
    rowServerFilter
  ])

  const { data: dataColumns } = useFetchEntitiesOfType(
    columnEntityType,
    columnArgs
  )
  const { data: dataRows } = useFetchEntitiesOfType(rowEntityType, rowArgs)

  const [localValue, setLocalValue] = useState(props.defaultValue)

  useEffect(() => {
    props.changeHandler?.(localValue)
  }, [localValue])

  const toggleCheckState = (row, column) => {
    setLocalValue((prev) => {
      const newValue = prev?.filter((item) => item.rowId !== row.id) ?? []
      newValue.push({
        rowId: row.id,
        rowDisplayName: row.display_name,
        columnId: column.id,
        columnDisplayName: column.display_name
      })
      return newValue
    })
  }

  const columnsToShow = dataColumns?.slice(0, 5)

  return (
    <View style={{ flex: 1 }}>
      <DataTable style={{ flex: 1 }}>
        <DataTable.Header>
          <DataTable.Title></DataTable.Title>
          {columnsToShow?.map((column) => (
            <DataTable.Title key={column.id}>
              {column.display_name}
            </DataTable.Title>
          ))}
        </DataTable.Header>
        <ScrollView>
          {dataRows?.map((row) => (
            <DataTable.Row key={row.id}>
              <DataTable.Cell>{row.display_name}</DataTable.Cell>
              {columnsToShow?.map((column) => (
                <DataTable.Cell key={column.id}>
                  <MSFESCheckbox
                    disabled={!editable}
                    checked={
                      localValue?.some(
                        (item) =>
                          item.rowId === row.id && item.columnId === column.id
                      ) ?? false
                    }
                    onPress={() => {
                      toggleCheckState(row, column)
                    }}
                  />
                </DataTable.Cell>
              ))}
            </DataTable.Row>
          ))}
        </ScrollView>
      </DataTable>
    </View>
  )
}
export const RadioTable = React.forwardRef(_RadioTable)
